'use client';

import * as React from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { IconBell01 } from 'icon/Alerts&feedback/bell-01';
import { IconCheckDone01 } from 'icon/General/check-done-01';
import {
  commentPollingInterval,
  fetchCommentRead,
  retryCommentRead,
} from 'lib/comment-read';
import type { CommentNotificationList, CommentViewer } from 'lib/comments';
import { useFormatter, useTranslations } from 'next-intl';
import { Popover, PopoverContent, PopoverTrigger } from 'ui';
import { useCommentMutation } from './comment-api';
import { CommentAvatar } from './comment-avatar';
import { CommentErrorMessage } from './comment-identity';

export function CommentNotifications({
  postId,
  manage,
  viewer,
  loading,
  onSignIn,
}: {
  postId: string;
  manage: boolean;
  viewer: CommentViewer | null;
  loading: boolean;
  onSignIn: () => void;
}) {
  const t = useTranslations('Blog.Comments');
  const client = useQueryClient();
  const format = useFormatter();
  const [open, setOpen] = React.useState(false);
  const unreadId = React.useId();
  const mutation = useCommentMutation(postId, manage);
  const query = useInfiniteQuery({
    queryKey: ['comment-notifications', viewer?.email, postId, manage],
    initialPageParam: 0,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({
        view: 'notifications',
        postId,
        manage: String(manage),
        page: String(pageParam),
      });
      return fetchCommentRead<CommentNotificationList>(
        `/.quotion/api/comments?${params}`,
        signal,
      );
    },
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled: !!viewer,
    meta: { commentPolling: !!viewer },
    refetchInterval: (query) =>
      viewer
        ? commentPollingInterval(
            client,
            query.state.error,
            open ? 15000 : 60000,
          )
        : false,
    retry: retryCommentRead,
  });
  const unread = query.data?.pages[0]?.unread ?? 0;
  const notifications = Array.from(
    new Map(
      query.data?.pages
        .flatMap((page) => page.notifications)
        .map((notification) => [notification.id, notification]),
    ).values(),
  );
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        if (value && !viewer) onSignIn();
        else setOpen(value);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={loading}
          aria-label={t('Notifications')}
          aria-describedby={unread ? unreadId : undefined}
          title={t('Notifications')}
          className="relative inline-flex size-9 items-center justify-center rounded-full text-tertiary hover:bg-secondary-hover hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-40"
        >
          <IconBell01 aria-hidden="true" className="size-5" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute end-1.5 top-1.5 size-1.5 rounded-full bg-brand-600 ring-2 ring-primary"
            />
          )}
          <span id={unreadId} className="sr-only">
            {t('UnreadNotifications', { count: unread })}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        aria-label={t('Notifications')}
        className="w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border-secondary bg-primary p-0 text-primary"
      >
        <div className="flex items-center justify-between border-b border-secondary px-4 py-2.5">
          <h3 className="text-sm font-semibold">{t('Notifications')}</h3>
          {unread > 0 && (
            <button
              type="button"
              aria-label={t('MarkAllRead')}
              title={t('MarkAllRead')}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ action: 'read-notifications' })}
              className="inline-flex size-7 items-center justify-center rounded text-tertiary hover:bg-secondary-hover hover:text-primary focus-visible:outline-2 focus-visible:outline-brand-500 disabled:opacity-40"
            >
              <IconCheckDone01 aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>
        <div className="max-h-[min(24rem,60dvh)] overflow-y-auto">
          {query.isPending && (
            <p
              className="px-4 py-8 text-center text-sm text-tertiary"
              role="status"
            >
              {t('Loading')}
            </p>
          )}
          {query.error && (
            <div className="space-y-2 p-4">
              <CommentErrorMessage code={query.error.message} />
              <button
                type="button"
                onClick={() => query.refetch()}
                className="text-sm text-secondary hover:underline"
              >
                {t('Retry')}
              </button>
            </div>
          )}
          {query.data && notifications.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-tertiary">
              {t('NoNotifications')}
            </p>
          )}
          {notifications.map((notification) => {
            const name = notification.author?.name ?? t('Someone');
            const title = t(
              notification.kind === 'reply'
                ? 'NotificationReply'
                : notification.kind === 'like'
                  ? 'NotificationLike'
                  : 'NotificationComment',
              { name },
            );
            const content = (
              <>
                <CommentAvatar
                  name={name}
                  image={notification.author?.avatar ?? null}
                  className="size-7 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-secondary">{title}</p>
                  {notification.preview && (
                    <p className="mt-1 line-clamp-2 break-words text-xs text-tertiary">
                      {notification.preview}
                    </p>
                  )}
                  <p className="mt-1.5 truncate text-xs text-quaternary">
                    {notification.postTitle || t('PrivatePost')} ·{' '}
                    {notification.domain}
                  </p>
                  <time
                    className="mt-1 block text-xs text-quaternary"
                    dateTime={notification.createdAt}
                  >
                    {format.dateTime(new Date(notification.createdAt), {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </time>
                </div>
                {!notification.read && (
                  <span
                    className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-600"
                    aria-label={t('Unread')}
                  />
                )}
              </>
            );
            const className = `flex items-start gap-3 border-b border-secondary px-4 py-3 text-start last:border-0 ${notification.read ? '' : 'bg-secondary/50'}`;
            return notification.url ? (
              <a
                key={notification.id}
                href={notification.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${className} hover:bg-secondary-hover focus-visible:outline-2 focus-visible:outline-brand-500`}
                onClick={() => {
                  if (!notification.read)
                    mutation.mutate({
                      action: 'read-notifications',
                      notificationId: notification.id,
                    });
                }}
              >
                {content}
              </a>
            ) : (
              <div key={notification.id} className={className}>
                {content}
              </div>
            );
          })}
          {query.hasNextPage && (
            <button
              type="button"
              disabled={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
              className="w-full py-3 text-xs text-secondary hover:bg-secondary-hover"
            >
              {query.isFetchingNextPage ? t('Loading') : t('LoadMore')}
            </button>
          )}
        </div>
        {mutation.error && (
          <div className="px-4 py-2">
            <CommentErrorMessage code={mutation.error.message} />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
